import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import SM from 'services/ServiceManager';
import ServerError from 'errors/ServerError';
import { DocStatus } from 'components/DocBox/DocBox.types';
import { JAUsersType } from 'datasource/useApplicationDetails/ApplicationDetailsManager.types';

/**
 * Fetches the ID document for each joint-account user in parallel.
 * Returns the raw per-user responses; the consumer maps them into the reducer shape.
 * Enabled only when there are users to fetch for.
 */
export const useGetIdDocuments = ({
    jaUsers, language, isEnabled = false,
}: { jaUsers?: JAUsersType, language: string, isEnabled?: boolean }) => {
    const userIds = (jaUsers ?? []).map((user) => user.id);

    const result = useQuery({
        queryKey: ['getIdDocuments', userIds, language],
        enabled: isEnabled && userIds.length > 0,
        retry: false,
        refetchOnWindowFocus: false,
        staleTime: 30 * 60 * 1000, // 30 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes
        queryFn: async () => Promise.all((jaUsers as JAUsersType).map((user) => (SM.customDocumentsService(
            'getDocumentsByContactId',
            [{ contactId: user.id, language }],
        ) as Promise<AxiosResponse>).then((res) => (res?.data ? ({ ...res.data, userId: user?.id }) : {
            id: null,
            name: null,
            createDate: null,
            userId: user?.id,
            status: DocStatus.ReadyToSign,
        })))),
    });

    return {
        ...result,
        error: result.error ? new ServerError(result.error) : result.error,
    };
};
